"use client";

import { ArrowLeft, ArrowRight, CalendarDays, Check, Eraser, FileImage, Mail, MapPin, PenLine, Phone, Upload, UserRound } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Image from "next/image";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { membershipPlan, pricingLicenseNote, pricingPaymentNote } from "@/data/pricing";

const inputClass = "mt-2 h-12 border-border bg-background/70";

type FormState = {
  plan: typeof membershipPlan.id;
  firstName: string;
  lastName: string;
  birthDate: string;
  email: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  experience: string;
  isMinor: boolean;
  parentName: string;
  parentPhone: string;
  photo: File | null;
};

const initialForm: FormState = {
  plan: membershipPlan.id,
  firstName: "",
  lastName: "",
  birthDate: "",
  email: "",
  phone: "",
  address: "",
  postalCode: "",
  city: "",
  experience: "",
  isMinor: false,
  parentName: "",
  parentPhone: "",
  photo: null,
};

const stepLabels = ["Coordonnées", "Tarif unique", "Dossier final"];

async function normalizePhoto(file: File) {
  if (typeof createImageBitmap === "undefined") return file;

  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
  return blob ? new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg", lastModified: file.lastModified }) : file;
}

function Field({ label, icon: Icon, className, type, ...props }: React.ComponentProps<typeof Input> & { label: string; icon?: React.ComponentType<{ className?: string }> }) {
  const dateClass = type === "date" ? "appearance-none pr-9 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-2 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0" : "";
  const showDatePlaceholder = type === "date" && !props.value;
  return <label className={`block min-w-0 max-w-full ${className ?? ""}`}><div className="relative min-w-0 max-w-full"><Input aria-label={label} className={`${inputClass} w-full min-w-0 max-w-full text-foreground ${Icon ? "pl-10" : ""} ${dateClass} ${showDatePlaceholder ? "[&::-webkit-datetime-edit]:text-transparent" : ""}`} lang={type === "date" ? "fr-FR" : undefined} placeholder={label} type={type} {...props} />{Icon ? <Icon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /> : null}{showDatePlaceholder ? <span aria-hidden="true" className="pointer-events-none absolute left-10 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">JJ/MM/AAAA</span> : null}</div></label>;
}

type SignaturePadHandle = {
  toBlob: () => Promise<Blob | null>;
};

const SignaturePad = forwardRef<SignaturePadHandle, { onChange: (value: boolean) => void }>(function SignaturePad({ onChange }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);

  useImperativeHandle(ref, () => ({
    toBlob: () => new Promise((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        resolve(null);
        return;
      }
      canvas.toBlob(resolve, "image/png");
    }),
  }), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.strokeStyle = "#b52a3d";
    context.lineWidth = 2.5;
    context.lineCap = "round";
    context.lineJoin = "round";
  }, []);

  function point(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const bounds = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - bounds.left) * (canvas.width / bounds.width),
      y: (event.clientY - bounds.top) * (canvas.height / bounds.height),
    };
  }

  function start(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    canvas.setPointerCapture(event.pointerId);
    const { x, y } = point(event);
    context.beginPath();
    context.moveTo(x, y);
    drawingRef.current = true;
  }

  function draw(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const context = canvasRef.current?.getContext("2d");
    if (!context) return;
    const { x, y } = point(event);
    context.lineTo(x, y);
    context.stroke();
    onChange(true);
  }

  function clear() {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    onChange(false);
  }

  return (
    <div className="mt-3 overflow-hidden border border-white/12 bg-[#0b0b0b] shadow-[0_10px_30px_rgba(0,0,0,.18)]">
      <canvas
        aria-label="Zone de signature"
        className="block h-32 w-full touch-none bg-[linear-gradient(rgba(255,255,255,.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.025)_1px,transparent_1px)] [background-size:24px_24px] sm:h-36"
        height="144"
        onPointerDown={start}
        onPointerMove={draw}
        onPointerUp={() => { drawingRef.current = false; }}
        onPointerCancel={() => { drawingRef.current = false; }}
        ref={canvasRef}
        width="720"
      />
      <div className="flex items-center justify-between border-t border-white/10 px-3 py-2.5 text-[.58rem] font-bold uppercase tracking-[.12em] text-white/72">
        <span className="flex items-center gap-2"><PenLine className="size-3.5 text-primary" /> Signe dans le cadre</span>
        <button className="flex min-h-8 items-center gap-1.5 px-2 transition-colors hover:bg-white/8 hover:text-white" onClick={clear} type="button"><Eraser className="size-3.5" /> Effacer</button>
      </div>
    </div>
  );
});

export function RegistrationForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const signatureRef = useRef<SignaturePadHandle>(null);
  const parentSignatureRef = useRef<SignaturePadHandle>(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [signed, setSigned] = useState(false);
  const [parentSigned, setParentSigned] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const reduceMotion = useReducedMotion();

  function update(values: Partial<FormState>) {
    setForm((current) => ({ ...current, ...values }));
  }

  function goNext() {
    if (formRef.current?.reportValidity()) setStep((current) => current + 1);
  }

  function goBack() {
    if (step === 3) {
      setSigned(false);
      setParentSigned(false);
    }
    setStep((current) => current - 1);
  }

  function resetForm() {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setForm(initialForm);
    setPhotoPreview(null);
    setSigned(false);
    setParentSigned(false);
    setSubmitted(false);
    setSubmitError(null);
    setStep(1);
  }

  async function handlePhoto(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    setSubmitError(null);

    if (file && (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 2 * 1024 * 1024)) {
      event.target.value = "";
      update({ photo: null });
      setPhotoPreview(null);
      setSubmitError("La photo doit être au format JPG ou PNG et ne pas dépasser 2 Mo.");
      return;
    }

    const normalizedFile = file ? await normalizePhoto(file) : null;
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    update({ photo: normalizedFile });
    setPhotoPreview(normalizedFile ? URL.createObjectURL(normalizedFile) : null);
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const website = new FormData(event.currentTarget).get("website");
    setSubmitError(null);
    setSubmitting(true);

    try {
      const [signature, parentSignature] = await Promise.all([
        signatureRef.current?.toBlob(),
        form.isMinor ? parentSignatureRef.current?.toBlob() : Promise.resolve(null),
      ]);

      if (!signature || (form.isMinor && !parentSignature)) {
        throw new Error("Les signatures demandées sont requises.");
      }

      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key !== "photo") payload.append(key, String(value));
      });
      payload.append("website", String(website ?? ""));
      if (form.photo) payload.append("photo", form.photo);
      payload.append("signature", signature, "signature-pratiquant.png");
      if (parentSignature) payload.append("parentSignature", parentSignature, "signature-responsable.png");

      const response = await fetch("/api/registrations", { method: "POST", body: payload });
      const result = (await response.json().catch(() => ({}))) as { error?: string };

      if (!response.ok) {
        throw new Error(result.error ?? "L’envoi du dossier a échoué.");
      }

      setSubmitted(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "L’envoi du dossier a échoué.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return <div className="border border-primary/40 bg-card p-7 sm:p-10"><div className="grid size-14 place-items-center bg-primary text-white"><Check /></div><p className="eyebrow mt-8"><span />Dossier envoyé</p><h2 className="mt-4 font-display text-5xl font-black uppercase leading-none">On se retrouve<br /><span className="text-primary">sur le tapis.</span></h2><p className="mt-5 max-w-lg text-sm leading-7 text-muted-foreground">Ton dossier a bien été transmis à l’équipe. Tu vas recevoir un email de confirmation avec le planning et tout ce qu’il faut prévoir pour ton cours d’essai. Pense à vérifier tes courriers indésirables.</p><Button className="mt-7" onClick={resetForm} type="button" variant="outline">Envoyer un autre dossier</Button></div>;
  }

  return (
    <form className="w-full min-w-0 border border-border bg-card shadow-[0_12px_30px_rgba(0,0,0,.16)]" onSubmit={submit} ref={formRef}>
      <div className="border-b border-border bg-muted/40 px-4 py-4 sm:px-8 sm:py-5"><div className="flex items-center justify-between gap-3"><div><p className="text-[.58rem] font-extrabold uppercase tracking-[.16em] text-primary">Dossier d’adhésion</p><p className="mt-1 font-display text-xl font-black uppercase sm:text-2xl">Étape 0{step} <span className="text-muted-foreground">/ 03</span></p><p className="mt-1 text-[.58rem] font-bold uppercase tracking-[.1em] text-muted-foreground">{stepLabels[step - 1]}</p></div><div className="flex gap-1"><span className={`h-1 w-8 sm:w-20 ${step >= 1 ? "bg-primary" : "bg-border"}`} /><span className={`h-1 w-8 sm:w-20 ${step >= 2 ? "bg-primary" : "bg-border"}`} /><span className={`h-1 w-8 sm:w-20 ${step >= 3 ? "bg-primary" : "bg-border"}`} /></div></div></div>
      <div className="p-4 sm:p-8">
        <AnimatePresence initial={false} mode="wait">
        <motion.div
          animate={reduceMotion ? undefined : { opacity: 1, x: 0 }}
          exit={reduceMotion ? undefined : { opacity: 0, x: step === 1 ? -18 : 18 }}
          initial={reduceMotion ? false : { opacity: 0, x: step === 1 ? 18 : -18 }}
          key={step}
          transition={{ duration: 0.28, ease: "easeOut" }}
        >
        {step === 1 ? <div className="space-y-4 sm:space-y-8">
          <div><h2 className="flex items-center gap-3 font-display text-2xl font-black uppercase sm:text-3xl"><span className="grid size-8 place-items-center bg-primary text-white sm:size-9"><UserRound className="size-4" /></span>Tes informations</h2><p className="mt-2 text-[.8rem] leading-5 text-muted-foreground sm:text-sm sm:leading-6">Commence par nous laisser tes coordonnées pour préparer ton accueil.</p></div>
          <div className="grid grid-cols-1 gap-2.5 min-[480px]:grid-cols-2 sm:gap-5"><Field icon={UserRound} label="Prénom" onChange={(event) => update({ firstName: event.target.value })} required value={form.firstName} /><Field icon={UserRound} label="Nom" onChange={(event) => update({ lastName: event.target.value })} required value={form.lastName} /><Field icon={CalendarDays} label="Date de naissance" onChange={(event) => update({ birthDate: event.target.value })} required type="date" value={form.birthDate} /><Field icon={Phone} label="Téléphone" onChange={(event) => update({ phone: event.target.value })} required type="tel" value={form.phone} /><Field className="min-[480px]:col-span-2" icon={Mail} label="Email" onChange={(event) => update({ email: event.target.value })} required type="email" value={form.email} /></div>
        </div> : step === 2 ? <div className="space-y-4 sm:space-y-8">
          <div>
            <h2 className="flex items-center gap-3 font-display text-2xl font-black uppercase sm:text-3xl"><span className="grid size-8 place-items-center bg-primary text-white sm:size-9"><Check className="size-4" /></span>Ton adhésion</h2>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">{membershipPlan.description}</p>
          </div>
          <div className="border border-primary bg-primary/5 p-5 shadow-[0_8px_24px_rgba(181,42,61,.12)] sm:p-6">
            <p className="text-xs font-bold uppercase tracking-[.12em] text-primary">Tarif unique · {membershipPlan.audience}</p>
            <h3 className="mt-3 font-display text-3xl font-black uppercase">{membershipPlan.title}</h3>
            <p className="mt-5 font-display text-5xl font-black">{membershipPlan.amount} € <span className="font-sans text-sm font-semibold text-muted-foreground">/ an</span></p>
            <p className="mt-3 text-sm font-bold text-primary">{pricingPaymentNote}</p>
            <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground"><Check className="size-4 shrink-0 text-primary" />{pricingLicenseNote}</p>
          </div>
        </div> : <div className="space-y-8">
          <div><h2 className="font-display text-3xl font-black uppercase">Les dernières infos</h2><p className="mt-2 text-sm leading-6 text-muted-foreground">Ces éléments nous permettent de préparer ton accueil et ton dossier.</p></div>
          <div className="grid gap-5 sm:grid-cols-2"><Field className="sm:col-span-2" icon={MapPin} label="Adresse" onChange={(event) => update({ address: event.target.value })} required value={form.address} /><Field icon={MapPin} label="Code postal" onChange={(event) => update({ postalCode: event.target.value })} required value={form.postalCode} /><Field icon={MapPin} label="Ville" onChange={(event) => update({ city: event.target.value })} required value={form.city} /><label className="block min-w-0 sm:col-span-2"><div className="relative"><select aria-label="Niveau / expérience" className="h-12 w-full appearance-none border border-border bg-background/70 pl-10 pr-4 text-sm font-medium text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" onChange={(event) => update({ experience: event.target.value })} value={form.experience}><option value="">Niveau / expérience</option><option value="Débutant">Débutant</option><option value="Pratiquant">Pratiquant</option><option value="Compétition">Compétition</option></select><PenLine className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /></div></label></div>
          <label className="flex cursor-pointer items-start gap-3 border border-border p-4 text-sm font-semibold"><input checked={form.isMinor} className="mt-1 size-4 accent-primary" onChange={(event) => { update({ isMinor: event.target.checked }); setParentSigned(false); }} type="checkbox" /> <span>Le pratiquant est mineur.<small className="mt-1 block font-medium text-muted-foreground">Les informations du responsable légal seront demandées.</small></span></label>
          {form.isMinor && <div className="grid gap-5 border-l-2 border-primary pl-4 sm:grid-cols-2"><Field icon={UserRound} label="Nom du responsable légal" onChange={(event) => update({ parentName: event.target.value })} required value={form.parentName} /><Field icon={Phone} label="Téléphone du responsable" onChange={(event) => update({ parentPhone: event.target.value })} required type="tel" value={form.parentPhone} /><div className="sm:col-span-2"><p className="flex items-center gap-2 text-sm font-semibold"><PenLine className="size-3.5 text-primary" />Signature du responsable légal</p><SignaturePad onChange={setParentSigned} ref={parentSignatureRef} /></div></div>}
          <div className="grid gap-6 sm:grid-cols-2"><div><p className="text-sm font-semibold">Photo d’identité</p><label className="mt-2 flex min-h-36 cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-border bg-background/60 p-4 text-center text-xs font-bold text-muted-foreground hover:border-primary"><input accept="image/jpeg,image/png" capture="environment" className="sr-only" onChange={handlePhoto} type="file" />{photoPreview ? <Image unoptimized alt="Aperçu de la photo sélectionnée" className="size-24 object-cover" height={96} src={photoPreview} width={96} /> : <FileImage className="size-7 text-primary" />}<span>{form.photo?.name ?? "Prendre une photo ou ajouter un fichier"}</span><span className="flex items-center gap-1 text-[.6rem] uppercase tracking-[.1em]"><Upload className="size-3" /> Appareil photo ou galerie · JPG/PNG · 2 Mo max.</span></label></div><div><p className="text-sm font-semibold">Signature du pratiquant</p><SignaturePad onChange={setSigned} ref={signatureRef} /><p className="mt-2 text-[.65rem] leading-5 text-muted-foreground">En signant, je confirme l’exactitude des informations transmises.</p></div></div>
        </div>}
        </motion.div>
        </AnimatePresence>
        <input aria-hidden="true" autoComplete="off" className="hidden" name="website" tabIndex={-1} type="text" />
        {submitError ? <p aria-live="polite" className="mt-5 border-l-2 border-primary bg-primary/5 px-4 py-3 text-sm font-semibold text-foreground">{submitError}</p> : null}
        <div className="mt-6 flex flex-col-reverse gap-3 border-t border-border pb-2 pt-5 sm:mt-9 sm:flex-row sm:justify-between sm:pb-0 sm:pt-6">{step > 1 ? <Button disabled={submitting} onClick={goBack} type="button" variant="outline"><ArrowLeft /> Retour</Button> : <span />}{step < 3 ? <Button onClick={goNext} size="xl" type="button">Continuer <ArrowRight /></Button> : <Button disabled={submitting || !signed || (form.isMinor && !parentSigned)} size="xl" type="submit">{submitting ? "Envoi en cours…" : "Envoyer mon dossier"} <Check /></Button>}</div>
      </div>
    </form>
  );
}
